import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, Filter, AlertTriangle, ExternalLink, StickyNote, MessageSquareText, Loader2, Copy, Trash2, Sparkles } from "lucide-react";
import Modal from "@/components/Modal";

const APPLIED_STATES = ["applied", "interview", "offer", "rejected"];

const STATUSES = ["evaluated", "applied", "skipped", "rejected", "interview", "offer"];
const statusStyle = {
  evaluated: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  applied: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  skipped: "bg-slate-800 text-slate-400 border-slate-700",
  rejected: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  interview: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  offer: "bg-emerald-400/20 text-emerald-300 border-emerald-400/40",
};

export default function Tracker() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "executive";
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({
    client_id: params.get("client") || "", decision: "", status: "", work_mode: "",
    min_score: "", search: "", date_from: "", date_to: "",
  });
  const [noteRow, setNoteRow] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [qaRow, setQaRow] = useState(null);
  const [qaList, setQaList] = useState([]);
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaLength, setQaLength] = useState("medium");
  const [qaLoading, setQaLoading] = useState(false);

  const load = () => {
    const qs = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) qs.append(k, v); });
    api.get(`/evaluations?${qs.toString()}`).then((r) => setRows(r.data));
  };

  useEffect(() => { api.get("/clients?include_archived=true").then((r) => setClients(r.data)); }, []);
  useEffect(() => { load(); }, [f]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  // duplicate detection: mark rows that share company+title with an earlier row for the same client
  const dupIds = useMemo(() => {
    const seen = new Map();
    const dups = new Set();
    [...rows].reverse().forEach((r) => {
      const key = `${r.client_id}|${(r.company || "").toLowerCase()}|${(r.job_title || "").toLowerCase()}`;
      if (seen.has(key)) dups.add(r.id);
      else seen.set(key, r.id);
    });
    return dups;
  }, [rows]);

  const counts = useMemo(() => ({
    evaluated: rows.length,
    applied: rows.filter((r) => r.status === "applied").length,
    interviews: rows.filter((r) => r.status === "interview").length,
    offers: rows.filter((r) => r.status === "offer").length,
  }), [rows]);

  const updateStatus = async (row, status) => {
    await api.put(`/evaluations/${row.id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete this evaluation for "${row.job_title || "this job"}"? This can't be undone.`)) return;
    try {
      await api.delete(`/evaluations/${row.id}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      toast.success("Entry deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveNote = async () => {
    await api.put(`/evaluations/${noteRow.id}`, { notes: noteText });
    toast.success("Note saved");
    setNoteRow(null);
    load();
  };

  const openQa = (row) => {
    setQaRow(row);
    setQaList(row.qa || []);
    setQaQuestion("");
    setQaLength("medium");
  };

  const genQa = async () => {
    if (qaQuestion.trim().length < 5) { toast.error("Type the application question first"); return; }
    setQaLoading(true);
    try {
      const { data } = await api.post(`/evaluations/${qaRow.id}/answer`, { question: qaQuestion, length: qaLength });
      setQaList(data.all);
      setQaQuestion("");
      setRows((rs) => rs.map((r) => (r.id === qaRow.id ? { ...r, qa: data.all } : r)));
      toast.success("Answer generated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setQaLoading(false); }
  };

  const delQa = async (qaId) => {
    try {
      const { data } = await api.delete(`/evaluations/${qaRow.id}/answer/${qaId}`);
      setQaList(data.all);
      setRows((rs) => rs.map((r) => (r.id === qaRow.id ? { ...r, qa: data.all } : r)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const exportCsv = () => {
    const token = localStorage.getItem("ap_token");
    const url = `${API}/evaluations-export${f.client_id ? `?client_id=${f.client_id}` : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "applypilot_tracker.csv";
        a.click();
      })
      .catch((e) => toast.error(apiError(e)));
  };

  const sel = "bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 transition-colors";

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight">Application Tracker</h1>
          <p className="text-slate-400 text-sm mt-1">Every evaluated job, logged automatically. Filter, update status, export.</p>
        </div>
        <button data-testid="export-csv" onClick={exportCsv} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 px-4 py-2.5 rounded-lg transition-colors">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["Evaluated", counts.evaluated, "text-blue-400"], ["Applied", counts.applied, "text-purple-400"], ["Interviews", counts.interviews, "text-amber-400"], ["Offers", counts.offers, "text-emerald-400"]].map(([l, v, c]) => (
          <div key={l} className="bg-[#12151F] border border-slate-800/80 rounded-xl px-5 py-4">
            <div className={`font-mono font-bold text-2xl ${c}`}>{v}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{l}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-slate-500" />
        <select data-testid="filter-client" className={sel} value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}>
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="filter-decision" className={sel} value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })}>
          <option value="">Any decision</option>
          <option value="APPLY">APPLY</option>
          <option value="DO NOT APPLY">DO NOT APPLY</option>
        </select>
        <select data-testid="filter-status" className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-workmode" className={sel} value={f.work_mode} onChange={(e) => setF({ ...f, work_mode: e.target.value })}>
          <option value="">Any mode</option>
          <option value="remote">remote</option>
          <option value="hybrid">hybrid</option>
          <option value="onsite">onsite</option>
        </select>
        <input data-testid="filter-minscore" type="number" placeholder="Min score" className={`${sel} w-28`} value={f.min_score} onChange={(e) => setF({ ...f, min_score: e.target.value })} />
        <input data-testid="filter-search" placeholder="Search company/title" className={`${sel} flex-1 min-w-[160px]`} value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} />
        <input data-testid="filter-datefrom" type="date" className={sel} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <input data-testid="filter-dateto" type="date" className={sel} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
      </div>

      {/* Table */}
      <div className="bg-[#12151F] border border-slate-800/80 rounded-xl shadow-lg shadow-black/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D0E15] text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                {["Client", "Job", "Location / Mode", "Salary", "Sponsorship", "Score", "Decision", "Status", ""].map((h) => (
                  <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`eval-row-${r.id}`} className="hover:bg-slate-800/25 transition-colors">
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{clientName(r.client_id)}</td>
                  <td className="px-4 py-3 w-full min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-slate-200 font-medium flex items-center gap-1.5">
                          {r.job_title}
                          {dupIds.has(r.id) && (
                            <span title="Duplicate: same company + title already logged" className="text-amber-400"><AlertTriangle className="w-3.5 h-3.5" /></span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          {r.company}
                          {r.job_url && <a href={r.job_url} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300"><ExternalLink className="w-3 h-3" /></a>}
                        </div>
                        {r.resume_label && (
                          <span data-testid={`resume-tag-${r.id}`} className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border border-indigo-500/30 bg-indigo-500/10 text-indigo-300">
                            {r.resume_label}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{r.location || "—"}<div className="text-xs text-slate-600">{r.work_mode}</div></td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{r.salary || <span className="text-slate-600">not stated</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-xs ${r.sponsorship === "supported" ? "text-emerald-400" : r.sponsorship === "not supported" ? "text-rose-400" : "text-slate-500"}`}>{r.sponsorship}</span>
                  </td>
                  <td className="px-4 py-3 font-mono font-bold text-slate-200">{r.fit_score}</td>
                  <td className="px-4 py-3">
                    <span className={r.decision === "APPLY"
                      ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap"
                      : "bg-rose-500/15 text-rose-400 border border-rose-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase whitespace-nowrap"}>
                      {r.decision}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select data-testid={`status-select-${r.id}`} value={r.status} onChange={(e) => updateStatus(r, e.target.value)}
                      className={`text-xs border rounded-md px-2 py-1 outline-none ${statusStyle[r.status] || statusStyle.evaluated}`}>
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0D0E15] text-slate-200">{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button data-testid={`qa-btn-${r.id}`} onClick={() => openQa(r)} title="Application Q&A"
                        className="relative text-slate-500 hover:text-indigo-300 transition-colors">
                        <MessageSquareText className="w-4 h-4" />
                        {(r.qa || []).length > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-indigo-500 text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">{(r.qa || []).length}</span>
                        )}
                      </button>
                      <button data-testid={`note-btn-${r.id}`} onClick={() => { setNoteRow(r); setNoteText(r.notes || ""); }} title="Notes" className="text-slate-500 hover:text-indigo-300 transition-colors">
                        <StickyNote className="w-4 h-4" />
                      </button>
                      {user?.role === "admin" && (
                        <button data-testid={`delete-btn-${r.id}`} onClick={() => deleteRow(r)} title="Delete entry" className="text-slate-500 hover:text-rose-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-12">No evaluations match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {noteRow && (
        <Modal onClose={() => setNoteRow(null)}>
          <div className="bg-[#141724] border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl p-6">
            <h3 className="font-heading font-semibold text-slate-100">Notes — {noteRow.job_title}</h3>
            <textarea data-testid="note-textarea" rows={5} value={noteText} onChange={(e) => setNoteText(e.target.value)}
              className="mt-4 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60 transition-colors" />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setNoteRow(null)} className="text-sm text-slate-400 hover:text-slate-200 px-4 py-2">Cancel</button>
              <button data-testid="save-note" onClick={saveNote} className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">Save note</button>
            </div>
          </div>
        </Modal>
      )}
      {qaRow && (
        <Modal onClose={() => setQaRow(null)}>
          <div className="bg-[#141724] border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-800/60">
              <div>
                <h3 className="font-heading font-semibold text-slate-100 flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-indigo-400" /> Application Q&amp;A</h3>
                <p className="text-xs text-slate-500 mt-0.5">{qaRow.job_title} · {qaRow.company}</p>
              </div>
              <button data-testid="close-qa" onClick={() => setQaRow(null)} className="text-slate-500 hover:text-slate-200 text-xl leading-none">×</button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              {isStaff && (
                APPLIED_STATES.includes(qaRow.status) ? (
                  <div className="bg-[#0D0E15] border border-slate-800 rounded-xl p-4">
                    <label className="text-xs text-slate-500">Paste the application question</label>
                    <textarea data-testid="qa-question" rows={2} value={qaQuestion} onChange={(e) => setQaQuestion(e.target.value)}
                      placeholder="e.g. Why do you want to join this company?"
                      className="mt-1 w-full bg-[#141724] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60" />
                    <div className="flex items-center gap-3 mt-3">
                      <select data-testid="qa-length" value={qaLength} onChange={(e) => setQaLength(e.target.value)}
                        className="bg-[#141724] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-indigo-500/60">
                        <option value="short">Short</option>
                        <option value="medium">Medium</option>
                        <option value="long">Long</option>
                      </select>
                      <button data-testid="qa-generate" onClick={genQa} disabled={qaLoading}
                        className="ml-auto flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
                        {qaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate answer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">
                    Mark this job as <b>applied</b> to generate application answers.
                  </div>
                )
              )}

              {qaList.length === 0 ? (
                <div className="text-center text-slate-500 text-sm py-8">No application questions answered yet.</div>
              ) : (
                <div className="space-y-3">
                  {qaList.map((q) => (
                    <div key={q.id} data-testid={`qa-item-${q.id}`} className="bg-[#0D0E15] border border-slate-800 rounded-xl p-4">
                      <div className="text-sm font-semibold text-indigo-300">{q.question}</div>
                      <div className="text-sm text-slate-300 mt-2 whitespace-pre-wrap leading-relaxed">{q.answer}</div>
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-[11px] text-slate-600 uppercase tracking-wide">{q.length}</span>
                        <button onClick={() => copyText(q.answer)} className="ml-auto flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-300"><Copy className="w-3.5 h-3.5" /> Copy</button>
                        {isStaff && <button data-testid={`qa-delete-${q.id}`} onClick={() => delQa(q.id)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
